import axios from 'axios';
import { authCookies } from '../utils/cookies';

// Base URL from env — fall back to /api. When no backend is reachable
// the service files below ship mock data so the UI keeps working.
const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// ── Request interceptor: attach token ─────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = authCookies.getToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (err) => Promise.reject(err)
);

// ── Response interceptor: central error handling ──────────────────────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error.response) {
      // Network / CORS / timeout
      error.isNetworkError = true;
      // Deferred import to avoid circular deps with stores
      import('../store/uiStore').then(({ useUiStore }) => {
        useUiStore.getState().pushToast({
          type: 'error',
          title: 'Network error',
          message: 'Unable to reach the server. Check your connection.',
        });
      });
      return Promise.reject(error);
    }

    const { status } = error.response;

    if (status === 401) {
      // Token expired or invalid — clear session and bounce to login.
      authCookies.clear();
      import('../store/authStore').then(({ useAuthStore }) => {
        useAuthStore.getState().clearSession();
      });
      if (
        typeof window !== 'undefined' &&
        !['/login', '/register', '/intro'].some((p) => window.location.pathname.startsWith(p))
      ) {
        window.location.href = '/login';
      }
    } else if (status >= 500) {
      import('../store/uiStore').then(({ useUiStore }) => {
        useUiStore.getState().pushToast({
          type: 'error',
          title: 'Server error',
          message: 'Something went wrong on our side. Please try again.',
        });
      });
    } else if (status >= 400) {
      const message =
        error.response.data?.message ||
        error.response.data?.error ||
        'Request failed';
      import('../store/uiStore').then(({ useUiStore }) => {
        useUiStore.getState().pushToast({
          type: 'error',
          title: 'Error',
          message,
        });
      });
    }

    return Promise.reject(error);
  }
);

export default api;
